/**
 * Runtime Value Resolution
 * 
 * Provides functions to resolve static and dynamic values from templates
 * using selected entities and override values.
 */
import { mediaRegistry, entityRegistry } from "../../template-editor/registry";
import { dynamicKey, Overrides } from "../../template-editor/utils/bindingUtils";
import { getByPath } from "../../template-editor/utils/pathUtils";
import { Slot, TextContentValue, StaticOrDynamic } from "../template.schemas";

/**
 * Selected entities data structure with slots
 */
export type SelectedEntities = Record<string, any> & { slots?: Record<string, any> };

/**
 * Resolution context containing selected entities and override values
 */
export type ResolveContext = {
  /** Selected entities data (e.g., { slots:{ playerA:{...} } }) */
  selected: SelectedEntities;
  /** Override values for specific paths (e.g., { 'slots.playerA.name': 'abc', ... }) */
  overrides: Overrides;
  /** Manual overrides specific to slot attributes (slotId -> attributePath -> value) */
  manualSlotOverrides?: Record<string, Record<string, any>>;
  /** Template slots for entity default value resolution */
  templateSlots?: Slot[];
};

/**
 * Gets the default value for an entity attribute from the entity registry
 * 
 * @param entityType - The entity type (e.g., 'Player', 'Team')
 * @param attributePath - The attribute path (e.g., 'name', 'logoPath')
 * @returns Default value from entity definition or undefined
 */
function getEntityAttributeDefault(entityType: string, attributePath: string): any {
  const entityDef = entityRegistry.get(entityType);
  if (!entityDef) return undefined;
  
  const attribute = entityDef.attributes.find(attr => attr.attributeKey === attributePath);
  return attribute?.defaultValue;
}

/**
 * Resolves a StaticOrDynamic value using the provided context
 * 
 * @param spec - Static or dynamic value specification
 * @param ctx - Resolution context with entities and overrides
 * @returns Resolved value or undefined if not found
 */
export function resolveStaticOrDynamic<T>(spec: StaticOrDynamic<T>, ctx: ResolveContext): T | undefined {
  const mt = spec.mediaType;
  const fmt = spec.format;
  const renderer = mediaRegistry.get(mt);
  if (!renderer) {
    console.warn(`No media renderer found for type: ${mt}`);
    return undefined;
  }
  const apply = renderer.applyFormat;

  if (spec.mode === 'static') {
    return apply(spec.staticValue, fmt);
  }

  // dynamic mode
  const src = spec.dynamic.source;
  
  if (src.kind === 'slot') {
     console.debug(`[Resolve] Checking slot source: ${src.slotId}.${src.attributePath}`, 
       'Has manual override?', 
       ctx.manualSlotOverrides?.[src.slotId]?.[src.attributePath] !== undefined,
       'ManualSlotOverrides keys:', ctx.manualSlotOverrides ? Object.keys(ctx.manualSlotOverrides) : 'none'
     );
  }
  
  // 1. Check manual slot overrides first (highest priority)
  if (src.kind === 'slot' && ctx.manualSlotOverrides?.[src.slotId]?.[src.attributePath] !== undefined) {
    console.debug('Manual slot override found for slot:', spec, ctx);
    const overrideVal = ctx.manualSlotOverrides[src.slotId][src.attributePath];
    // Special handling if override value is null (explicitly cleared) -> return undefined or handle? 
    // Usually manual override means "use this value".
    return apply(overrideVal, fmt);
  }

  // 2. Check generic overrides
  const key = dynamicKey(src);
  if (ctx.overrides && key in ctx.overrides) {
    return apply(ctx.overrides[key], fmt);
  }

  let raw: any;
  if (src.kind === 'slot') {
    const slotData = ctx.selected?.slots?.[src.slotId];
    raw = slotData ? getByPath(slotData, src.attributePath) : undefined;
  } else if (src.kind === 'selector') {
    raw = ctx.selected?.selectors?.[src.selectorId];
  }
  if (raw !== undefined) return apply(raw, fmt);

  if (spec.dynamic.defaultValue !== undefined) {
    return apply(spec.dynamic.defaultValue, fmt);
  }

  if (src.kind === 'slot' && ctx.templateSlots) {
    const slot = ctx.templateSlots.find(s => s.id === src.slotId);
    if (slot) {
      const defaultValue = getEntityAttributeDefault(slot.entityType, src.attributePath);
      if (defaultValue !== undefined) {
        return apply(defaultValue, fmt);
      }
    }
  }

  return undefined;
}

/**
 * Enhanced version of resolveStaticOrDynamic that always returns a fallback value for preview
 * 
 * @param spec - Static or dynamic value specification
 * @param ctx - Resolution context with entities and overrides
 * @returns Resolved value with fallback for preview
 */
export function resolveStaticOrDynamicWithPreview<T>(spec: StaticOrDynamic<T>, ctx: ResolveContext): T;

/**
 * Overload for TextContentValue - handles flexible text content with multiple media types
 * 
 * @param spec - Text content value specification (union of different media types)
 * @param ctx - Resolution context with entities and overrides
 * @returns Resolved string value with fallback for preview
 */
export function resolveStaticOrDynamicWithPreview(spec: TextContentValue, ctx: ResolveContext): string;

export function resolveStaticOrDynamicWithPreview<T>(spec: StaticOrDynamic<T> | TextContentValue, ctx: ResolveContext): T | string {
  if ('mediaType' in spec) {
    if (['text', 'date', 'int', 'float'].includes(spec.mediaType)) {
      return resolveTextContentWithPreview(spec, ctx);
    }
    if (spec.mediaType === 'image') {
      return resolveImageSource(spec as StaticOrDynamic<any>, ctx) as T;
    }
  }

  const regularSpec = spec as StaticOrDynamic<T>;
  const resolved = resolveStaticOrDynamic(regularSpec, ctx);
  if (resolved !== undefined) return resolved;

  const mt = regularSpec.mediaType;
  const renderer = mediaRegistry.get(mt);
  if (!renderer) {
    console.warn(`No media renderer found for type: ${mt}`);
    return '[Wert]' as T;
  }

  let fallbackRaw: any;
  switch (mt) {
    case 'text':  fallbackRaw = '[Text]'; break;
    case 'image': fallbackRaw = '/defaults/image_placeholder.png'; break;
    case 'date':  fallbackRaw = '[Datum]'; break;
    case 'color': fallbackRaw = '#CCCCCC'; break;
    case 'int':   fallbackRaw = 42; break;
    case 'float': fallbackRaw = 3.14; break;
    default:      fallbackRaw = '[Wert]';
  }

  return renderer.applyFormat(fallbackRaw, regularSpec.format) as T;
}

/**
 * Reference to a dynamic field in a template
 */
export type DynamicFieldRef = {
  /** Stable field identifier (e.g., `${elementId}.content` / `${elementId}.source`) */
  fieldId: string;
  /** Element ID containing this field */
  elementId: string;
  /** Media type of the field */
  mediaType: string;
  /** Binding reference source */
  source: any;
  /** Optional display label */
  label?: string;
  /** Optional description */
  description?: string;
};

/**
 * Extracts all dynamic fields from a template
 * 
 * @param template - Template object to scan
 * @returns Array of dynamic field references
 */
export function collectDynamicFields(template: any): DynamicFieldRef[] {
  const out: DynamicFieldRef[] = [];
  
  // Scan template elements
  for (const el of template.elements ?? []) {
    // Text elements with dynamic content
    if (el.type === 'text' && el.content?.mode === 'dynamic') {
      out.push({
        fieldId: `${el.id}.content`,
        elementId: el.id,
        mediaType: el.content.mediaType,
        source: el.content.dynamic.source,
        label: el.content.dynamic.label,
        description: el.content.dynamic.description,
      });
    }
    // Text color
    if (el.type === 'text' && el.color?.mode === 'dynamic') {
      out.push({
        fieldId: `${el.id}.color`,
        elementId: el.id,
        mediaType: el.color.mediaType,
        source: el.color.dynamic.source,
        label: el.color.dynamic.label,
        description: el.color.dynamic.description,
      });
    }
    // Image elements with dynamic source
    if (el.type === 'image' && el.source?.mode === 'dynamic') {
      out.push({
        fieldId: `${el.id}.source`,
        elementId: el.id,
        mediaType: el.source.mediaType,
        source: el.source.dynamic.source,
        label: el.source.dynamic.label,
        description: el.source.dynamic.description,
      });
    }
    // Rect/Circle fill color
    if ((el.type === 'rect' || el.type === 'circle') && el.fill?.kind === 'solid' && el.fill.color?.mode === 'dynamic') {
      out.push({
        fieldId: `${el.id}.fill.color`,
        elementId: el.id,
        mediaType: el.fill.color.mediaType,
        source: el.fill.color.dynamic.source,
        label: el.fill.color.dynamic.label,
        description: el.fill.color.dynamic.description,
      });
    }
    // Rect/Circle gradient stops colors
    if ((el.type === 'rect' || el.type === 'circle') && el.fill?.kind === 'gradient' && Array.isArray(el.fill.stops)) {
      el.fill.stops.forEach((stop: any, idx: number) => {
        if (stop?.color?.mode === 'dynamic') {
          out.push({
            fieldId: `${el.id}.fill.stops.${idx}.color`,
            elementId: el.id,
            mediaType: stop.color.mediaType,
            source: stop.color.dynamic.source,
            label: stop.color.dynamic.label,
            description: stop.color.dynamic.description,
          });
        }
      });
    }
    // Stroke color for rect/circle/line
    if ((el.type === 'rect' || el.type === 'circle' || el.type === 'line') && el.stroke?.color?.mode === 'dynamic') {
      out.push({
        fieldId: `${el.id}.stroke.color`,
        elementId: el.id,
        mediaType: el.stroke.color.mediaType,
        source: el.stroke.color.dynamic.source,
        label: el.stroke.color.dynamic.label,
        description: el.stroke.color.dynamic.description,
      });
    }

    // SVG elements
    if (el.type === 'svg') {
      // Source
      if (el.source?.mode === 'dynamic') {
        out.push({
          fieldId: `${el.id}.source`,
          elementId: el.id,
          mediaType: el.source.mediaType,
          source: el.source.dynamic.source,
          label: el.source.dynamic.label,
          description: el.source.dynamic.description,
        });
      }
      // Monochrome Color
      if (el.monochromeColor?.mode === 'dynamic') {
        out.push({
          fieldId: `${el.id}.monochromeColor`,
          elementId: el.id,
          mediaType: el.monochromeColor.mediaType,
          source: el.monochromeColor.dynamic.source,
          label: el.monochromeColor.dynamic.label,
          description: el.monochromeColor.dynamic.description,
        });
      }
      // Color Mapping
      if (el.colorMapping) {
        Object.entries(el.colorMapping).forEach(([originalColor, value]: [string, any]) => {
          if (value?.mode === 'dynamic') {
            out.push({
              fieldId: `${el.id}.colorMapping.${originalColor}`,
              elementId: el.id,
              mediaType: value.mediaType,
              source: value.dynamic.source,
              label: value.dynamic.label || `SVG Farbe ${originalColor}`,
              description: value.dynamic.description,
            });
          }
        });
      }
    }
  }

  // Scan repeater children (if present)
  for (const rep of template.repeaters ?? []) {
    for (const child of rep.children ?? []) {
      if (child.type === 'text' && child.content?.mode === 'dynamic') {
        out.push({
          fieldId: `${rep.id}.${child.id}.content`,
          elementId: child.id,
          mediaType: child.content.mediaType,
          source: child.content.dynamic.source,
          label: child.content.dynamic.label,
          description: child.content.dynamic.description,
        });
      }
      if (child.type === 'image' && child.source?.mode === 'dynamic') {
        out.push({
          fieldId: `${rep.id}.${child.id}.source`,
          elementId: child.id,
          mediaType: child.source.mediaType,
          source: child.source.dynamic.source,
          label: child.source.dynamic.label,
          description: child.source.dynamic.description,
        });
      }
      if (child.type === 'text' && child.color?.mode === 'dynamic') {
        out.push({
          fieldId: `${rep.id}.${child.id}.color`,
          elementId: child.id,
          mediaType: child.color.mediaType,
          source: child.color.dynamic.source,
          label: child.color.dynamic.label,
          description: child.color.dynamic.description,
        });
      }
      if ((child.type === 'rect' || child.type === 'circle') && child.fill?.kind === 'solid' && child.fill.color?.mode === 'dynamic') {
        out.push({
          fieldId: `${rep.id}.${child.id}.fill.color`,
          elementId: child.id,
          mediaType: child.fill.color.mediaType,
          source: child.fill.color.dynamic.source,
          label: child.fill.color.dynamic.label,
          description: child.fill.color.dynamic.description,
        });
      }
      if ((child.type === 'rect' || child.type === 'circle') && child.fill?.kind === 'gradient' && Array.isArray(child.fill.stops)) {
        child.fill.stops.forEach((stop: any, idx: number) => {
          if (stop?.color?.mode === 'dynamic') {
            out.push({
              fieldId: `${rep.id}.${child.id}.fill.stops.${idx}.color`,
              elementId: child.id,
              mediaType: stop.color.mediaType,
              source: stop.color.dynamic.source,
              label: stop.color.dynamic.label,
              description: stop.color.dynamic.description,
            });
          }
        });
      }
      if ((child.type === 'rect' || child.type === 'circle' || child.type === 'line') && child.stroke?.color?.mode === 'dynamic') {
        out.push({
          fieldId: `${rep.id}.${child.id}.stroke.color`,
          elementId: child.id,
          mediaType: child.stroke.color.mediaType,
          source: child.stroke.color.dynamic.source,
          label: child.stroke.color.dynamic.label,
          description: child.stroke.color.dynamic.description,
        });
      }

      // SVG in repeaters
      if (child.type === 'svg') {
        if (child.source?.mode === 'dynamic') {
          out.push({
            fieldId: `${rep.id}.${child.id}.source`,
            elementId: child.id,
            mediaType: child.source.mediaType,
            source: child.source.dynamic.source,
            label: child.source.dynamic.label,
            description: child.source.dynamic.description,
          });
        }
        if (child.monochromeColor?.mode === 'dynamic') {
          out.push({
            fieldId: `${rep.id}.${child.id}.monochromeColor`,
            elementId: child.id,
            mediaType: child.monochromeColor.mediaType,
            source: child.monochromeColor.dynamic.source,
            label: child.monochromeColor.dynamic.label,
            description: child.monochromeColor.dynamic.description,
          });
        }
        if (child.colorMapping) {
          Object.entries(child.colorMapping).forEach(([originalColor, value]: [string, any]) => {
            if (value?.mode === 'dynamic') {
              out.push({
                fieldId: `${rep.id}.${child.id}.colorMapping.${originalColor}`,
                elementId: child.id,
                mediaType: value.mediaType,
                source: value.dynamic.source,
                label: value.dynamic.label || `SVG Farbe ${originalColor}`,
                description: value.dynamic.description,
              });
            }
          });
        }
      }
    }
  }

  return out;
}

/**
 * Special resolver for text content that can handle multiple media types
 * Always returns a string representation regardless of the underlying media type
 * 
 * @param spec - Text content value with flexible media type (text, date, int, float)
 * @param ctx - Resolution context with entities and overrides
 * @returns Resolved value as string with fallback for preview
 */
export function resolveTextContentWithPreview(spec: any, ctx: ResolveContext): string {
  const mt = spec.mediaType;
  const fmt = spec.format;
  const renderer = mediaRegistry.get(mt);
  if (!renderer) {
    console.warn(`No media renderer found for type: ${mt}`);
    return '[Text]';
  }

  const resolved = resolveTextContent(spec, ctx);
  if (resolved !== undefined) return resolved;

  let fallbackRaw: any;
  switch (mt) {
    case 'text':  fallbackRaw = '[Text]'; break;
    case 'date':  fallbackRaw = '[Datum]'; break;
    case 'int':   fallbackRaw = 42; break;
    case 'float': fallbackRaw = 3.14; break;
    default:      fallbackRaw = '[Text]';
  }

  const result = renderer.applyFormat(fallbackRaw, fmt);
  return typeof result === 'string' ? result : String(result);
}

/**
 * Special resolver for text content that can handle multiple media types
 * 
 * @param spec - Text content value with flexible media type (text, date, int, float)
 * @param ctx - Resolution context with entities and overrides
 * @returns Resolved value as string or undefined if not found
 */
export function resolveTextContent(spec: any, ctx: ResolveContext): string | undefined {
  const mt = spec.mediaType;
  const fmt = spec.format;
  const renderer = mediaRegistry.get(mt);
  if (!renderer) {
    console.warn(`No media renderer found for type: ${mt}`);
    return undefined;
  }
  const apply = renderer.applyFormat;

  if (spec.mode === 'static') {
    const result = apply(spec.staticValue, fmt);
    return typeof result === 'string' ? result : String(result);
  }

  const src = spec.dynamic.source;
  
  // 1. Check manual slot overrides
  if (src.kind === 'slot' && ctx.manualSlotOverrides?.[src.slotId]?.[src.attributePath] !== undefined) {
    const overrideVal = ctx.manualSlotOverrides[src.slotId][src.attributePath];
    const result = apply(overrideVal, fmt);
    return typeof result === 'string' ? result : String(result);
  }

  // 2. Check generic overrides
  const key = dynamicKey(src);
  if (ctx.overrides && key in ctx.overrides) {
    const result = apply(ctx.overrides[key], fmt);
    return typeof result === 'string' ? result : String(result);
  }

  let raw: any;
  if (src.kind === 'slot') {
    const slotData = ctx.selected?.slots?.[src.slotId];
    raw = slotData ? getByPath(slotData, src.attributePath) : undefined;
  } else if (src.kind === 'selector') {
    raw = ctx.selected?.selectors?.[src.selectorId];
  } else if (src.kind === 'path') {
    raw = getByPath(ctx.selected, src.path);
  }
  if (raw !== undefined) {
    const result = apply(raw, fmt);
    return typeof result === 'string' ? result : String(result);
  }

  if (spec.dynamic.defaultValue !== undefined) {
    const result = apply(spec.dynamic.defaultValue, fmt);
    return typeof result === 'string' ? result : String(result);
  }

  if (src.kind === 'slot' && ctx.templateSlots) {
    const slot = ctx.templateSlots.find(s => s.id === src.slotId);
    if (slot) {
      const defaultValue = getEntityAttributeDefault(slot.entityType, src.attributePath);
      if (defaultValue !== undefined) {
        const result = apply(defaultValue, fmt);
        return typeof result === 'string' ? result : String(result);
      }
    }
  }

  return undefined;
}

/**
 * Resolves an image source URL from a StaticOrDynamic specification.
 * 
 * @param spec - Static or dynamic image source specification
 * @param ctx - Resolution context with entities and overrides
 * @returns Resolved image URL or undefined if not found
 */
export function resolveImageSource(spec: StaticOrDynamic<any>, ctx: ResolveContext): string {
  const DEFAULT_PATH = "/defaults/image_placeholder.png";
  const normalizeUrl = (value: string): string => {
    const trimmed = value.trim();
    return trimmed.replace(/^["']+/, '').replace(/["']+$/, '').trim();
  };
  if (!spec) {
    return DEFAULT_PATH;
  }

  if (spec.mode === "static") {
    const v: any = spec.staticValue as any;
    if (typeof v === "string") return normalizeUrl(v);
    if (v && typeof v === "object") {
      if (v.url) return normalizeUrl(String(v.url));
      if (v.path) return normalizeUrl(String(v.path));
      if (v.downloadUrl) return normalizeUrl(String(v.downloadUrl));
    }
    return DEFAULT_PATH;
  }

  const { source } = spec.dynamic;
  
  // 1. Check manual slot overrides
  if (source.kind === 'slot' && ctx.manualSlotOverrides?.[source.slotId]?.[source.attributePath] !== undefined) {
     const ov: any = ctx.manualSlotOverrides[source.slotId][source.attributePath];
     if (typeof ov === "string") return normalizeUrl(ov);
     if (ov && typeof ov === "object") {
        if (ov.url) return normalizeUrl(String(ov.url));
        if (ov.path) return normalizeUrl(String(ov.path));
        if (ov.downloadUrl) return normalizeUrl(String(ov.downloadUrl));
        // Note: mediaId is not a URL, so we might need handling? But assuming it resolves to URL in upload.
     }
  }

  const key = dynamicKey(source);
  if (ctx.overrides && key in ctx.overrides) {
    const ov: any = (ctx.overrides as any)[key];
    if (typeof ov === "string") return normalizeUrl(ov);
    if (ov && typeof ov === "object") {
      if (ov.url) return normalizeUrl(String(ov.url));
      if (ov.path) return normalizeUrl(String(ov.path));
      if (ov.downloadUrl) return normalizeUrl(String(ov.downloadUrl));
      if (ov.mediaId) return DEFAULT_PATH;
    }
  }

  let raw: any;
  if (source.kind === "slot") {
    const slotData = ctx.selected?.slots?.[source.slotId];
    raw = slotData ? getByPath(slotData, source.attributePath) : undefined;
  } else if (source.kind === "selector") {
    raw = ctx.selected?.selectors?.[source.selectorId];
  }
  if (raw !== undefined) {
    if (typeof raw === "string") return normalizeUrl(raw);
    if (raw && typeof raw === "object") {
      if (raw.url) return normalizeUrl(String(raw.url));
      if (raw.path) return normalizeUrl(String(raw.path));
      if (raw.downloadUrl) return normalizeUrl(String(raw.downloadUrl));
      if (raw.mediaId) return DEFAULT_PATH;
    }
  }

  if (spec.dynamic.defaultValue !== undefined) {
    const def: any = spec.dynamic.defaultValue as any;
    if (typeof def === "string") return normalizeUrl(def);
    if (def && typeof def === "object") {
      if (def.url) return normalizeUrl(String(def.url));
      if (def.path) return normalizeUrl(String(def.path));
      if (def.downloadUrl) return normalizeUrl(String(def.downloadUrl));
    }
  }

  if (source.kind === "slot" && ctx.templateSlots) {
    const slot = ctx.templateSlots.find((s) => s.id === source.slotId);
    if (slot) {
      const defaultValue = getEntityAttributeDefault(slot.entityType, source.attributePath);
      if (typeof defaultValue === "string") return defaultValue;
    }
  }

  return DEFAULT_PATH;
}
